import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { passwords } from "@/api/passwords";
import { ApiError } from "@/api/http";
import { Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token") ?? "";
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: () => passwords.reset(token, password),
    onSuccess: () => setDone(true),
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-center text-base font-semibold">Set a new password</h1>
        </CardHeader>
        <CardBody>
          {done ? (
            <div className="space-y-3 text-sm">
              <p className="text-emerald-700 dark:text-emerald-300">Password updated. You can sign in now.</p>
              <Link to="/login" className="text-brand">
                Go to sign in →
              </Link>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                if (password !== confirm) {
                  setError("Passwords don't match.");
                  return;
                }
                submit.mutate();
              }}
              className="space-y-3"
            >
              {!tokenFromUrl && (
                <div>
                  <Label>Reset token</Label>
                  <Input value={token} onChange={(e) => setToken(e.target.value)} required />
                </div>
              )}
              <div>
                <Label>New password</Label>
                <Input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              <FormError>{error}</FormError>
              <Button type="submit" loading={submit.isPending} className="w-full">
                Set password
              </Button>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
